/**
 * StartYourCirclePerk.jsx
 *
 * Perk card surfaced on:
 *   • /partner — Partner page  (Become a Partner / 3-tier MLM)
 *   • /referral — Refer-a-friend page
 *   • Team Alignment Hub (quiet pill variant, when no team yet)
 *
 * Logic
 * ─────
 *  • GETs /api/team-alignment/circles/eligibility on mount
 *  • If tier === null → "Refer a friend to unlock your Circle"
 *  • If tier === "refer_a_friend" → "Start your Circle · up to 5"
 *  • If tier === "partner" → "Start your Circle · unlimited"
 *  • If already_started → "View your Circle" → /team-alignment
 *
 * Aesthetic uses Aurum tokens so it sits beautifully on either
 * the dark partner dashboards or the lighter referral page.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Sparkles, Users, Crown, ArrowRight, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");

const TOK = {
  cream:    "#F3EDDD",
  creamDim: "rgba(243, 237, 221, 0.66)",
  gold:     "#A88A4C",
  goldSoft: "#D7BB7A",
  goldMist: "rgba(215, 187, 122, 0.18)",
};

export default function StartYourCirclePerk({ variant = "card" }) {
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStart]  = useState(false);
  const [name, setName]       = useState("");
  const [error, setError]     = useState(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await axios.get(`${API}/api/team-alignment/circles/eligibility`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError("Couldn't load your Circle status.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  async function startCircle() {
    setStart(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/api/team-alignment/circles/start`,
        { name: name.trim() || undefined },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      navigate("/team-alignment");
      // Re-read eligibility on return.
      setData((prev) => ({
        ...(prev || {}),
        already_started: true,
        circle: {
          id: r.data.id,
          name: r.data.name,
          invite_code: r.data.invite_code,
          member_count: r.data.member_count,
          member_cap: r.data.member_cap,
          tier: r.data.circle_tier,
        },
      }));
    } catch (e) {
      const det = e?.response?.data?.detail;
      setError(
        det === "circle_already_exists" ? "You've already started your Circle."
        : det === "must_be_partner_or_referrer" ? "Refer your first friend to unlock this perk."
        : "Couldn't start your Circle right now."
      );
    } finally {
      setStart(false);
    }
  }

  if (loading) {
    if (variant === "pill") return null;  // pill stays silent while loading
    return (
      <div
        className="rounded-2xl p-6 flex items-center gap-3"
        style={{ background: "rgba(215,187,122,0.04)", border: `1px solid ${TOK.goldMist}` }}
        data-testid="circle-perk-loading"
      >
        <Loader2 size={14} className="animate-spin" style={{ color: TOK.goldSoft }} />
        <p className="italic" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
          Checking your Circle status…
        </p>
      </div>
    );
  }

  if (!data) return null;

  /* ────────── PILL VARIANT (Hub) ────────── */
  if (variant === "pill") {
    if (data.already_started || !data.can_start) return null;
    return (
      <button
        type="button"
        onClick={() => navigate("/affiliate")}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
        style={{
          background: "rgba(215, 187, 122, 0.10)",
          border: `1px solid ${TOK.gold}`,
          color: TOK.goldSoft,
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.58rem",
          letterSpacing: "0.24em",
          textTransform: "uppercase",
        }}
        data-testid="circle-perk-pill"
      >
        <Sparkles size={10} /> Start your Circle
      </button>
    );
  }

  /* ────────── CARD VARIANT (Partner / Referral pages) ────────── */
  const tierLabel = data.tier === "partner" ? "Partner perk" : data.tier === "refer_a_friend" ? "Referrer perk" : "Locked perk";
  const capLine =
    data.tier === "partner"      ? "Unlimited members. Your full organisation can align."
    : data.tier === "refer_a_friend" ? "Up to 5 members. Your inner circle, daily."
    : "Refer your first friend to unlock this perk.";

  return (
    <div
      className="rounded-2xl p-7"
      style={{
        background:
          "radial-gradient(ellipse at 20% 0%, rgba(215,187,122,0.16) 0%, rgba(215,187,122,0.05) 40%, rgba(7,13,11,0.55) 100%)",
        border: `1px solid ${data.can_start ? TOK.gold : TOK.goldMist}`,
        boxShadow: data.can_start ? "0 0 30px rgba(245,224,173,0.08)" : "none",
      }}
      data-testid="circle-perk-card"
    >
      <div className="flex items-baseline justify-between mb-4">
        <p className="uppercase tracking-[0.36em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', fontSize: "0.58rem" }}>
          {data.tier === "partner" ? <Crown size={11} className="inline mr-1.5 align-[-1px]" /> : <Sparkles size={11} className="inline mr-1.5 align-[-1px]" />}
          {tierLabel}
        </p>
        <p className="italic" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.88rem" }}>
          {data.referrals_count} {data.referrals_count === 1 ? "referral" : "referrals"}
        </p>
      </div>

      <h3
        className="italic mb-2"
        style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.65rem", lineHeight: 1.3, fontWeight: 400 }}
      >
        {data.already_started ? "Your Circle is live." : "Start your own Circle."}
      </h3>
      <p
        className="italic mb-5"
        style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.02rem", lineHeight: 1.55 }}
      >
        {data.already_started
          ? <>Lead your team's daily collective alignment — your voice, your music, the field installed beneath the day.</>
          : <>Your own daily collective alignment. You become the leader · your team installs the field together.</>}
      </p>

      <p
        className="italic mb-6"
        style={{ color: data.can_start ? TOK.goldSoft : TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.98rem" }}
      >
        {capLine}
      </p>

      {/* If they've already started → "View your Circle" */}
      {data.already_started ? (
        <>
          <div
            className="rounded-xl p-4 mb-5"
            style={{ background: "rgba(215,187,122,0.06)", border: `1px solid ${TOK.goldMist}` }}
          >
            <p className="italic mb-1" style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.1rem" }}>
              {data.circle?.name}
            </p>
            <p className="uppercase tracking-[0.24em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', fontSize: "0.56rem" }}>
              <Users size={9} className="inline mr-1.5 align-[-1px]" />
              {data.circle?.member_count}{data.circle?.member_cap ? ` / ${data.circle.member_cap}` : ""} {data.circle?.member_count === 1 ? "member" : "members"} · invite{" "}
              <em style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", textTransform: "none", letterSpacing: "normal", color: TOK.cream }}>
                {data.circle?.invite_code}
              </em>
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/team-alignment")}
            className="w-full py-3.5 rounded-full uppercase inline-flex items-center justify-center gap-2"
            style={{
              background: "rgba(215, 187, 122, 0.22)",
              border: `1px solid ${TOK.gold}`,
              color: TOK.cream,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.66rem",
              letterSpacing: "0.28em",
            }}
            data-testid="circle-perk-view"
          >
            Enter your Circle <ArrowRight size={12} />
          </button>
        </>
      ) : data.can_start ? (
        <>
          {editing && (
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 48))}
              onKeyDown={(e) => { if (e.key === "Enter") startCircle(); }}
              placeholder={data.suggested_name}
              className="w-full bg-transparent px-4 py-3 rounded-xl italic mb-3"
              style={{
                color: TOK.cream,
                border: `1px solid ${TOK.goldMist}`,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "1.05rem",
              }}
              data-testid="circle-perk-name-input"
            />
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => editing ? startCircle() : setEditing(true)}
              disabled={starting}
              className="flex-1 py-3.5 rounded-full uppercase inline-flex items-center justify-center gap-2"
              style={{
                background: "rgba(215, 187, 122, 0.22)",
                border: `1px solid ${TOK.gold}`,
                color: TOK.cream,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.66rem",
                letterSpacing: "0.28em",
                opacity: starting ? 0.6 : 1,
                cursor: starting ? "wait" : "pointer",
              }}
              data-testid="circle-perk-start"
            >
              {starting ? "Opening the field…" : editing ? <>Open the Circle <ArrowRight size={12} /></> : "Start your Circle"}
            </button>
            {editing && (
              <button
                type="button"
                onClick={() => { setEditing(false); setName(""); }}
                className="py-3.5 px-5 rounded-full uppercase"
                style={{
                  color: TOK.creamDim,
                  border: `1px solid ${TOK.goldMist}`,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.62rem",
                  letterSpacing: "0.24em",
                }}
                data-testid="circle-perk-cancel"
              >
                Cancel
              </button>
            )}
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={() => navigate("/account")}
          className="w-full py-3.5 rounded-full uppercase inline-flex items-center justify-center gap-2"
          style={{
            background: "rgba(215, 187, 122, 0.08)",
            border: `1px solid ${TOK.goldMist}`,
            color: TOK.creamDim,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.66rem",
            letterSpacing: "0.28em",
          }}
          data-testid="circle-perk-refer"
        >
          Refer a friend to unlock <ArrowRight size={12} />
        </button>
      )}

      {error && (
        <p className="italic text-sm mt-3" style={{ color: "#E5A091", fontFamily: '"Cormorant Garamond", serif' }} data-testid="circle-perk-error">
          {error}
        </p>
      )}
    </div>
  );
}
